import React from 'react'
import { List, useTable, ShowButton, EditButton, DeleteButton, DateField, CreateButton } from '@refinedev/antd'
import { useUpdate, useList } from '@refinedev/core'
import { Table, Space, Tag, Switch } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, TableSurface, type Stat } from '../../theme/listkit'
import { Sparkles, ShieldCheck, FileText, Newspaper } from '../../components/icons'
import dayjs from 'dayjs'

type Release = Tables<'releases'>

export function ReleaseList() {
  // Sort by published_at desc. No `nullsFirst` option is threaded through
  // Refine's supabase data provider (it only ever calls
  // `query.order(field, { ascending })`), but that is exactly what puts
  // drafts (NULL published_at) first: Postgres/PostgREST's own default for a
  // bare descending ORDER BY — with no NULLS FIRST/LAST override — is NULLS
  // FIRST (the reverse of ascending's NULLS LAST default). So "drafts surface
  // at the top" falls out of the database default, not extra client code.
  const { tableProps } = useTable<Release>({
    resource: 'releases',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'published_at', order: 'desc' }] },
  })
  const { mutate: update } = useUpdate()
  const onRow = useRowShow('releases')

  // Bounded summary deck — pageSize:1 count-only queries (see
  // notification_rules/list.tsx for the same shape), so the deck never pulls
  // the whole table to the client just to count it.
  const { result: totalRes } = useList<Release>({
    resource: 'releases',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const { result: publishedRes } = useList<Release>({
    resource: 'releases',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    // Live = published AND the date has arrived; a scheduled release is not
    // yet visible to a member, so counting it here would overstate reach.
    filters: [{ field: 'published_at', operator: 'lte', value: new Date().toISOString() }],
  })
  const { result: draftRes } = useList<Release>({
    resource: 'releases',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'published_at', operator: 'null', value: true }],
  })
  const { result: inFeedRes } = useList<Release>({
    resource: 'releases',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'show_in_feed', operator: 'eq', value: true }],
  })
  const totalCount = totalRes?.total ?? 0
  const publishedCount = publishedRes?.total ?? 0
  const draftCount = draftRes?.total ?? 0
  const inFeedCount = inFeedRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Releases', value: totalCount.toLocaleString(), icon: Sparkles, tone: 'jaguar' },
    { label: 'Live', value: publishedCount.toLocaleString(), icon: ShieldCheck, tone: 'green' },
    { label: 'Drafts', value: draftCount.toLocaleString(), icon: FileText, tone: 'gold' },
    { label: 'In Feed', value: inFeedCount.toLocaleString(), icon: Newspaper, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: "Content · What's New",
        description:
          'Curated, human-written release notes for the mobile app — completely decoupled from when code actually shipped. A draft is invisible to members; publishing sets the date it appears under.',
        actions: <CreateButton />,
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Release>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<Release>
              dataIndex="published_at"
              title="Status"
              render={(publishedAt: string | null) => {
                // THREE states, not two. The read policy is
                // `published_at is not null AND published_at <= now()`, so a
                // future date is SCHEDULED — authored, dated, and still
                // invisible to members until the clock reaches it. Collapsing
                // that into "Published" would tell an admin their note is live
                // when nobody can see it yet.
                if (!publishedAt) return <Tag color="gold">Draft</Tag>
                if (dayjs(publishedAt).isAfter(dayjs())) return <Tag color="blue">Scheduled</Tag>
                return <Tag color="green">Published</Tag>
              }}
            />
            <Table.Column<Release>
              dataIndex="published_at"
              title="Published"
              sorter
              render={(value: string | null) =>
                value ? (
                  <DateField value={value} format="MMM D, YYYY h:mm A" />
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Release>
              dataIndex="show_in_feed"
              title="Shows in Feed"
              render={(value: boolean, record: Release) => (
                <Switch
                  checked={value}
                  onChange={(checked) =>
                    update({ resource: 'releases', id: record.id, values: { show_in_feed: checked } })
                  }
                />
              )}
            />
            <Table.Column<Release>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Release) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
